// 행 그리드 사진 칸(widget:'image' / 'annotated-image')의 표시 + 클릭 확대.
//
// 두 가지를 한 자리에 모은다:
//  ① 표시 크기 — 선언 `displayVariant`(미지정=전폭 / 'thumbnail'=카드 폭 30%). 값·계약은 그대로
//     두고 표시만 바꾸는 축이라, 지정하지 않은 기존 선언은 종전 전폭 그대로다(회귀 0).
//  ② 클릭 확대 — 값이 있으면 사진을 눌러 원본을 크게 본다(구 TBM HazardItemCard → PhotoViewer 동형).
//     라이트박스는 공용 base `Modal`(@safetyhub/ui)이다 — 포탈·Esc·오버레이 닫기·포커스 트랩·
//     aria-modal·opener 포커스 복원을 재구현하지 않기 위해서다(레포 모달 규약 정본).
//
// rc 패키지 규약: 시각값은 인라인 style(이 패키지에서 처음 쓰는 Tailwind 유틸은 소비 앱 JIT가
// 생성하지 않아 조용히 무스타일이 된다 — feedback-rc-package-tailwind-class-purge-inline-style).
// Modal에 넘기는 z 클래스만 예외인데, 그 값은 web·admin 양쪽 소스에 이미 존재해 생성된다.

import { useState, type CSSProperties, type ReactNode } from 'react';
import { Modal } from '@safetyhub/ui';
import {
  AnnotatedImageView,
  IMAGE_PLACEHOLDER_STYLE,
  IMAGE_THUMBNAIL_IMG_STYLE,
  IMAGE_THUMBNAIL_PLACEHOLDER_STYLE,
  IMAGE_THUMBNAIL_WRAP_STYLE,
  READONLY_IMAGE_STYLE,
  normalizeAnnotatedImageValue,
} from './AnnotatedImageView';
import { ImageWithFallback } from './ImageWithFallback';

/**
 * 확대 라이트박스의 겹침 층 — 행 그리드는 `modalTrigger` 모달(root `z-[70]`) **안에서도** 렌더될
 * 수 있어(반복 행 카드를 모달 안에 두는 양식), 그 위에 떠야 한다. 이 뷰어 위에 열리는 것은
 * 없으므로 한 층(서브 모달 `z-[80]`)으로 고정한다 — "층은 여는 쪽이 정한다" 규약.
 */
const LIGHTBOX_Z_CLASS = 'z-[80]';

/**
 * 클릭 확대 트리거 — **레이아웃 중립**이 요건이다. 버튼의 브라우저 기본값(테두리·패딩·배경·인라인
 * 배치)을 전부 지워, 이 래퍼가 있고 없고에 따라 카드 안 사진 자리가 달라지지 않게 한다.
 */
const ZOOM_BUTTON_STYLE: CSSProperties = {
  display: 'block',
  width: '100%',
  margin: 0,
  padding: 0,
  border: 0,
  background: 'none',
  font: 'inherit',
  textAlign: 'left',
  cursor: 'zoom-in',
};

/** 확대 본문 — 세로로 긴 사진은 패널 안에서 스크롤한다(축소하면 도형 오버레이가 흐려진다). */
const LIGHTBOX_BODY_STYLE: CSSProperties = { maxHeight: '70vh', overflow: 'auto' };

export interface ImageLightboxProps {
  /** 값 — URL 문자열 또는 AnnotatedImageValue(도형 오버레이는 확대에서도 유지된다). */
  value: unknown;
  /** 헤더 제목 겸 img alt — 보통 그 칸의 라벨. 비면 '사진'. */
  label?: string;
  open: boolean;
  onClose: () => void;
}

/**
 * 사진 1장 확대 보기. 표시는 `AnnotatedImageView` 한 벌을 그대로 재사용한다 — 축소 표시와 확대가
 * 다른 렌더러를 타면 도형이 어긋나거나 한쪽만 고쳐지는 갈라짐이 생긴다.
 */
export function ImageLightbox({ value, label, open, onClose }: ImageLightboxProps) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={label && label !== '' ? label : '사진'}
      size="xl"
      overlayZClassName={LIGHTBOX_Z_CLASS}
    >
      <div style={LIGHTBOX_BODY_STYLE}>
        <AnnotatedImageView value={value} alt={label ?? ''} />
      </div>
    </Modal>
  );
}

export interface RowImageFieldProps {
  /** 'image'=평면 이미지 / 'annotated-image'=도형 오버레이(값이 문자열이면 평면으로 degrade). */
  widget: 'image' | 'annotated-image';
  value: unknown;
  /** 칸 라벨 — img alt + 확대 모달 제목. */
  label?: string;
  /** 미지정=전폭(기존 결) / 'thumbnail'=카드 폭 30%. */
  displayVariant?: 'thumbnail';
  /**
   * 저작 캔버스에서 렌더 중임 — **모양은 실폼과 같게 두고 확대만 열리지 않는다**(photo 업로드
   * 버튼의 `inert = authoringCanvas === true` 선례 동형). 캔버스에서 모달이 뜨면 트리·속성 패널을
   * 덮어 저작이 끊긴다. 클릭은 그대로 위로 전파돼 위젯 선택은 살아 있다.
   */
  authoringCanvas?: boolean;
}

/**
 * 행 그리드 사진 칸 1개. 값이 없으면 회색 placeholder(크기는 표시 변형을 따른다), 있으면 사진 +
 * 클릭 확대. 입력이 아니라 표시 전용이라 `editable`과 무관하게 같은 것을 그린다.
 */
export function RowImageField({
  widget,
  value,
  label,
  displayVariant,
  authoringCanvas = false,
}: RowImageFieldProps) {
  const [zoomOpen, setZoomOpen] = useState(false);
  const thumbnail = displayVariant === 'thumbnail';
  // 값 유무 판정은 두 위젯 공용 정규화를 쓴다 — annotated-image는 값이 객체일 수 있어
  // 문자열 전제로 재구현하면 도형 있는 사진이 "없음"으로 떨어진다.
  const url = normalizeAnnotatedImageValue(value).url;

  // 표시 본체 ─ image는 정사각 crop(구 카드 동형), annotated-image는 폭만 줄이고 비율 유지
  // (0~1 정규화 도형 좌표는 잘라내면 가리키는 자리가 사라진다 — AnnotatedImageView IMG_STYLE 주석).
  let body: ReactNode;
  if (widget === 'annotated-image') {
    body = <AnnotatedImageView value={value} alt={label ?? ''} />;
  } else if (url === '') {
    body = (
      <div style={thumbnail ? IMAGE_THUMBNAIL_PLACEHOLDER_STYLE : IMAGE_PLACEHOLDER_STYLE}>
        사진 없음
      </div>
    );
  } else {
    // 로드 실패는 같은 발자국의 안내 칸으로 접는다(ImageWithFallback 헤더 — P6-d).
    // ⚠️ 확대(zoomable) 판정은 **값 유무**(url !== '')로 남긴다 — 로드 실패를 「확대할 것이 없음」으로
    // 바꾸면 버튼이 사라져 「사진이 있었다」는 사실까지 지워진다. 라이트박스 안도 같은 폴백을 쓴다
    // (ImageLightbox → AnnotatedImageView → 이 한 벌).
    body = (
      <ImageWithFallback
        src={url}
        alt={label ?? ''}
        style={thumbnail ? IMAGE_THUMBNAIL_IMG_STYLE : READONLY_IMAGE_STYLE}
      />
    );
  }

  // 값이 없으면 확대할 것이 없다 — placeholder는 누를 수 없다(빈 칸에 zoom-in 커서가 뜨면 거짓말).
  const zoomable = url !== '';
  const clickable = zoomable ? (
    <>
      <button
        type="button"
        style={ZOOM_BUTTON_STYLE}
        title="클릭하면 크게 봅니다"
        aria-label={`${label && label !== '' ? label : '사진'} 크게 보기`}
        onClick={() => {
          if (authoringCanvas) return;
          setZoomOpen(true);
        }}
      >
        {body}
      </button>
      <ImageLightbox
        value={value}
        label={label}
        open={zoomOpen}
        onClose={() => setZoomOpen(false)}
      />
    </>
  ) : (
    body
  );

  // 썸네일은 폭 30% 래퍼가 좌표계를 준다(안쪽 버튼·이미지·placeholder는 전부 그 100%).
  return thumbnail ? <div style={IMAGE_THUMBNAIL_WRAP_STYLE}>{clickable}</div> : <>{clickable}</>;
}

export default RowImageField;
